import React, { createContext, useContext, useMemo } from 'react';
import { useHomeOnboardingCardsActive } from './useHomeOnboardingCardsActive';
import { useOnlineOperatingSetup } from './useOnlineOperatingSetup';
import { useWeekOneSetup } from './useWeekOneSetup';
import { useStoreActivationEvidence } from './useStoreActivationEvidence';

type HomeOnboardingCardsValue = ReturnType<typeof useHomeOnboardingCardsActive> & {
  weekOne: ReturnType<typeof useWeekOneSetup>;
  onlineOperating: ReturnType<typeof useOnlineOperatingSetup>;
  evidence: ReturnType<typeof useStoreActivationEvidence>;
};

const HomeOnboardingCardsContext = createContext<HomeOnboardingCardsValue | null>(null);

type ProviderProps = {
  userId: string;
  isOwner: boolean;
  offerAllowed: boolean;
  children: React.ReactNode;
};

/** Una sola instancia de hooks B3/B4/B5 + evidencia compartida por pantalla Inicio. */
export function HomeOnboardingCardsProvider({
  userId,
  isOwner,
  offerAllowed,
  children,
}: ProviderProps) {
  const evidence = useStoreActivationEvidence({ userId, isOwner, offerAllowed });
  const weekOne = useWeekOneSetup(userId, evidence);
  const onlineOperating = useOnlineOperatingSetup(userId, evidence);
  const gates = useHomeOnboardingCardsActive({
    userId,
    isOwner,
    offerAllowed,
    weekOne,
    onlineOperating,
    evidence,
  });

  const value = useMemo(
    () => ({
      ...gates,
      weekOne,
      onlineOperating,
      evidence,
    }),
    [evidence, gates, onlineOperating, weekOne],
  );

  return (
    <HomeOnboardingCardsContext.Provider value={value}>
      {children}
    </HomeOnboardingCardsContext.Provider>
  );
}

export function useHomeOnboardingCardsContext(): HomeOnboardingCardsValue | null {
  return useContext(HomeOnboardingCardsContext);
}
